/**
 * Phase 24.1a — `Logo` promoted from the dashboard to `@heroui/react`.
 *
 * Commerce OS logo — web renderer. Three variants share the same circuit-icon
 * glyph:
 *   - `icon`       — square mark only (favicon, top-left of compact layouts)
 *   - `horizontal` — mark + "Commerce OS" wordmark side-by-side (TopBar)
 *   - `stacked`    — mark above the centered wordmark (login / register / MFA)
 *
 * The wordmark text colors auto-adjust based on the `onDark` prop so the
 * same component works on light and dark backgrounds.
 *
 * The RN apps render the same geometry through
 * `packages/rn-shared/src/components/Logo.tsx`. Keep them visually
 * aligned if either is tweaked.
 *
 * Consumed by: apps/dashboard (TopBar, LoginPage, RegisterPage, MfaPage) and
 * apps/pos-web (TopBar, Login, Enroll, PinSwitch).
 */
import {useId} from "react";

export type LogoVariant = "icon" | "horizontal" | "stacked";

export interface LogoRootProps {
  variant?: LogoVariant;
  /**
   * Edge length (in px) of the square icon mark. Wordmark scales
   * proportionally for horizontal/stacked. Defaults to a sensible size
   * per variant (icon: 96, horizontal: 40, stacked: 120).
   */
  size?: number;
  /**
   * Render the wordmark in white-on-dark colors. Has no effect on the
   * `icon` variant.
   */
  onDark?: boolean;
  /** Optional accessible label. Defaults to "Commerce OS". Ignored when `decorative` is true. */
  ariaLabel?: string;
  /**
   * When true, the logo is treated as purely decorative — no role or
   * aria-label is emitted and the SVG content is hidden from assistive
   * tech via `aria-hidden`. Use this when the logo sits inside an
   * already-labeled element (e.g. a button with its own `aria-label`)
   * so screen readers don't announce the brand twice.
   */
  decorative?: boolean;
  className?: string;
}

const COLORS = {
  bgStart: "#0D1B2A",
  bgEnd: "#1B3A4B",
  goldStart: "#E8B931",
  goldEnd: "#F0D060",
  white: "#FFFFFF",
  wordDark: "#0D1B2A",
  wordLightAccent: "#5A6E7F",
  wordOnDark: "#FFFFFF",
  wordOnDarkAccent: "#9AA5B1",
};

interface IconMarkProps {
  size: number;
  uid: string;
}

function IconMark({size, uid}: IconMarkProps) {
  const bgGrad = `bgGrad-${uid}`;
  const goldGrad = `goldGrad-${uid}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="presentation"
    >
      <defs>
        <linearGradient id={bgGrad} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={COLORS.bgStart} />
          <stop offset="100%" stopColor={COLORS.bgEnd} />
        </linearGradient>
        <linearGradient id={goldGrad} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={COLORS.goldStart} />
          <stop offset="100%" stopColor={COLORS.goldEnd} />
        </linearGradient>
      </defs>
      <rect width="200" height="200" rx="40" fill={`url(#${bgGrad})`} />
      <circle cx="100" cy="100" r="18" fill={`url(#${goldGrad})`} />
      <circle
        cx="100"
        cy="100"
        r="26"
        stroke={COLORS.goldStart}
        strokeWidth="1.5"
        fill="none"
        opacity="0.35"
      />
      <line x1="100" y1="74" x2="100" y2="48" stroke={COLORS.white} strokeWidth="3" strokeLinecap="round" />
      <line x1="100" y1="126" x2="100" y2="152" stroke={COLORS.white} strokeWidth="3" strokeLinecap="round" />
      <line x1="74" y1="100" x2="48" y2="100" stroke={COLORS.white} strokeWidth="3" strokeLinecap="round" />
      <line x1="126" y1="100" x2="152" y2="100" stroke={COLORS.white} strokeWidth="3" strokeLinecap="round" />
      <line x1="82" y1="82" x2="62" y2="62" stroke={COLORS.goldStart} strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <line x1="118" y1="82" x2="138" y2="62" stroke={COLORS.goldStart} strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <line x1="82" y1="118" x2="62" y2="138" stroke={COLORS.goldStart} strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <line x1="118" y1="118" x2="138" y2="138" stroke={COLORS.goldStart} strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <circle cx="100" cy="42" r="9" fill={COLORS.white} />
      <circle cx="100" cy="158" r="9" fill={COLORS.white} />
      <circle cx="42" cy="100" r="9" fill={COLORS.white} />
      <circle cx="158" cy="100" r="9" fill={COLORS.white} />
      <circle cx="57" cy="57" r="6.5" fill={COLORS.goldStart} opacity="0.7" />
      <circle cx="143" cy="57" r="6.5" fill={COLORS.goldStart} opacity="0.7" />
      <circle cx="57" cy="143" r="6.5" fill={COLORS.goldStart} opacity="0.7" />
      <circle cx="143" cy="143" r="6.5" fill={COLORS.goldStart} opacity="0.7" />
    </svg>
  );
}

export function LogoRoot({
  variant = "icon",
  size,
  onDark = false,
  ariaLabel = "Commerce OS",
  decorative = false,
  className,
}: LogoRootProps) {
  const uid = useId().replace(/:/g, "");
  // When decorative, expose nothing to assistive tech — the parent
  // element is already labeled and announcing "Commerce OS" again would
  // be redundant.
  const a11yProps = decorative
    ? ({"aria-hidden": true} as const)
    : ({role: "img", "aria-label": ariaLabel} as const);

  if (variant === "icon") {
    const s = size ?? 96;
    return (
      <span className={className} {...a11yProps} style={{display: "inline-flex"}}>
        <IconMark size={s} uid={uid} />
      </span>
    );
  }

  // Theme-aware by default (2026-07-12): the dark theme defines
  // `--logo-wordmark(-accent)` in @heroui/styles' variables.css, so the
  // wordmark stays readable when `[data-theme="dark"]` is active at
  // runtime — callers no longer need to thread theme state into `onDark`.
  // The var falls back to the historical light colors, and `onDark` still
  // forces the white set for permanently-dark contexts.
  const wordColor = onDark ? COLORS.wordOnDark : `var(--logo-wordmark, ${COLORS.wordDark})`;
  const accentColor = onDark
    ? COLORS.wordOnDarkAccent
    : `var(--logo-wordmark-accent, ${COLORS.wordLightAccent})`;

  if (variant === "horizontal") {
    const iconSize = size ?? 40;
    const wordSize = Math.round(iconSize * 0.65);
    const subSize = Math.round(iconSize * 0.42);
    return (
      <span
        className={className}
        {...a11yProps}
        style={{display: "inline-flex", alignItems: "center", gap: Math.round(iconSize * 0.3)}}
      >
        <IconMark size={iconSize} uid={uid} />
        <span style={{display: "inline-flex", flexDirection: "column", lineHeight: 1}}>
          <span
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontWeight: 700,
              fontSize: wordSize,
              color: wordColor,
              letterSpacing: "-0.5px",
            }}
          >
            Commerce
          </span>
          <span
            style={{
              fontFamily: '"Courier New", monospace',
              fontWeight: 400,
              fontSize: subSize,
              color: accentColor,
              letterSpacing: "5px",
              marginTop: Math.round(iconSize * 0.1),
            }}
          >
            OS
          </span>
        </span>
      </span>
    );
  }

  // stacked
  const iconSize = size ?? 120;
  const wordSize = Math.round(iconSize * 0.34);
  const subSize = Math.round(iconSize * 0.24);
  return (
    <span
      className={className}
      {...a11yProps}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: Math.round(iconSize * 0.18),
      }}
    >
      <IconMark size={iconSize} uid={uid} />
      <span style={{display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1}}>
        <span
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            fontSize: wordSize,
            color: wordColor,
            letterSpacing: "-1px",
          }}
        >
          Commerce
        </span>
        <span
          style={{
            fontFamily: '"Courier New", monospace',
            fontWeight: 400,
            fontSize: subSize,
            color: accentColor,
            letterSpacing: "8px",
            marginTop: Math.round(iconSize * 0.06),
          }}
        >
          OS
        </span>
        <span
          style={{
            display: "block",
            width: Math.round(iconSize * 0.33),
            height: Math.max(2, Math.round(iconSize * 0.025)),
            background: COLORS.goldStart,
            borderRadius: 2,
            marginTop: Math.round(iconSize * 0.05),
          }}
        />
      </span>
    </span>
  );
}
